import React from 'react'
import Layout from '@components/layout/Layout'
import { Globe, BookOpen, Users, Eye, FileText } from 'lucide-react'

const AboutPage: React.FC = () => {
  return (
    <Layout
      seo={{
        title: 'About - HammerMUD',
        description: 'Learn about HammerMUD, our core values of community, accessibility, and transparency, and meet Hammer - the solo developer creating a virtual third space for text adventure gaming.',
        type: 'website',
        keywords: ['about', 'HammerMUD', 'Hammer', 'solo developer', 'virtual third space', 'text adventure', 'accessibility', 'transparency']
      }}
    >
      <div className="bg-gray-900 min-h-screen">
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-blue-900">
          <div className="absolute inset-0 bg-[url('/api/placeholder/1920/1080')] opacity-5 bg-cover bg-center"></div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
            <div className="text-center">
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6">
                About <span className="text-blue-400">HammerMUD</span>
              </h1>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
                A passion project to create a virtual third space where text adventures thrive, 
                built with modern conveniences while staying true to the MUD spirit.
              </p>
            </div>
          </div>
        </section>

        {/* Mission Statement */}
        <section className="py-20 bg-gray-800 border-t border-gray-700">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white mb-8">Our Mission</h2>
              <div className="space-y-6 text-lg text-gray-300 leading-relaxed">
                <p>
                  HammerMUD is dedicated to revitalizing the Multi-User Dungeon (MUD) gaming experience 
                  for a new generation of players while honoring the rich traditions that made MUDs beloved.
                </p>
                <p>
                  We believe that text-based gaming offers a unique depth of immersion and creativity 
                  that modern graphics-heavy games often lack. Our goal is to prove that with the right 
                  technology and design philosophy, MUDs can thrive in today's gaming landscape.
                </p>
              </div>
            </div>

            <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-6 text-center">
              <h3 className="text-xl font-semibold text-blue-400 mb-3">Development Status</h3>
              <p className="text-gray-300">
                HammerMUD is currently in active development. We're building the foundation for what 
                will become a rich, immersive gaming experience. Stay tuned for updates as we progress 
                toward our alpha release.
              </p>
            </div>
          </div>
        </section>

        {/* Core Values Section */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white mb-4">Our Core Values</h2>
              <p className="text-gray-300 text-lg max-w-2xl mx-auto">
                These principles guide every decision we make in building HammerMUD
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mb-4">
                  <Globe className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Virtual Third Space</h3>
                <p className="text-gray-300">
                  Creating a welcoming digital environment where everyone can find community, belonging, and shared adventure.
                </p>
              </div>

              <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center mb-4">
                  <BookOpen className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Text Adventure Core</h3>
                <p className="text-gray-300">
                  Keeping text adventures at the heart of gameplay, enhanced by modern conveniences without losing the magic.
                </p>
              </div>

              <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center mb-4">
                  <Eye className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Immersive Experience</h3>
                <p className="text-gray-300">
                  Rich storytelling and deep environments that reward exploration and replayability over massive scope.
                </p>
              </div>

              <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                <div className="w-12 h-12 bg-orange-600 rounded-lg flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Accessibility</h3>
                <p className="text-gray-300">
                  Accessible to all - new players discovering MUDs for the first time and veterans who prefer text for accessibility.
                </p>
              </div>

              <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 md:col-span-2 lg:col-span-1">
                <div className="w-12 h-12 bg-cyan-600 rounded-lg flex items-center justify-center mb-4">
                  <FileText className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Transparency</h3>
                <p className="text-gray-300">
                  Clear roadmaps, open goals, active community feedback, and transparent development process.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Team Section */}
        <section className="py-20 bg-gray-800 border-t border-gray-700">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-white mb-6">Meet Hammer</h2>
            <p className="text-gray-300 text-lg mb-8">
              HammerMUD is a solo passion project developed by one person with a vision 
              for what MUDs can become in the modern era.
            </p>
            
            <div className="bg-gray-900 rounded-lg p-8 border border-gray-700 max-w-2xl mx-auto">
              <div className="w-24 h-24 rounded-full mx-auto mb-6 overflow-hidden border-4 border-blue-500">
                <img 
                  src="/hammer-icon.png" 
                  alt="Hammer - HammerMUD Creator"
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">Hammer</h3>
              <p className="text-blue-400 mb-4">Solo Developer & Creator</p>
              <p className="text-gray-300 leading-relaxed">
                Passionate about creating meaningful digital experiences, Hammer brings together 
                years of software development expertise with a deep love for text-based gaming 
                and community building. Every aspect of HammerMUD - from code to content to 
                community - is crafted with personal attention and care.
              </p>
            </div>

            <div className="mt-12 bg-blue-900/20 border border-blue-500/30 rounded-lg p-6 max-w-2xl mx-auto">
              <h3 className="text-lg font-semibold text-blue-400 mb-3">Not Currently Hiring</h3>
              <p className="text-gray-300">
                HammerMUD is intentionally a solo project to maintain creative vision and 
                sustainable development. While we're not hiring, the community's support 
                and feedback are invaluable to the project's success.
              </p>
            </div>
          </div>
        </section>

        {/* Expanded Core Values */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white mb-4">Our Values in Detail</h2>
              <p className="text-gray-300 text-lg max-w-2xl mx-auto">
                A deeper look at what drives HammerMUD's development and community
              </p>
            </div>

            <div className="space-y-16">
              {/* Virtual Third Space */}
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div className="order-2 md:order-1">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center mr-3">
                      <Globe className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-white">Virtual Third Space</h3>
                  </div>
                  <p className="text-gray-300 text-lg leading-relaxed mb-4">
                    Beyond home and work, people need a "third space" - a place to gather, connect, and belong. 
                    HammerMUD aims to be that digital gathering place where friendships form, stories unfold, 
                    and community thrives.
                  </p>
                  <p className="text-gray-300 leading-relaxed">
                    We're building more than a game; we're creating a home for people who love text-based 
                    adventures, collaborative storytelling, and the unique social dynamics that MUDs foster. 
                    Whether you're here for 20 minutes or 2 hours, you should feel welcomed and valued.
                  </p>
                </div>
                <div className="order-1 md:order-2 bg-gray-800 rounded-lg p-6 border border-gray-700">
                  <h4 className="text-lg font-semibold text-blue-400 mb-3">This Means:</h4>
                  <ul className="space-y-2 text-gray-300">
                    <li>• Inclusive community guidelines and moderation</li>
                    <li>• Social features that encourage interaction</li>
                    <li>• Spaces for both casual and dedicated players</li>
                    <li>• Regular community events and gatherings</li>
                  </ul>
                </div>
              </div>

              {/* Text Adventure Core */}
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                  <h4 className="text-lg font-semibold text-green-400 mb-3">This Means:</h4>
                  <ul className="space-y-2 text-gray-300">
                    <li>• Rich, detailed room descriptions and world-building</li>
                    <li>• Text-based combat with strategic depth</li>
                    <li>• Imagination-driven gameplay over flashy graphics</li>
                    <li>• Modern UI that enhances rather than replaces text</li>
                  </ul>
                </div>
                <div>
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center mr-3">
                      <BookOpen className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-white">Text Adventure Core</h3>
                  </div>
                  <p className="text-gray-300 text-lg leading-relaxed mb-4">
                    Text adventures have a unique power - they engage imagination in ways that graphics cannot. 
                    Every player's mental image of the world is personal and vivid, creating deeper immersion 
                    than any visual medium.
                  </p>
                  <p className="text-gray-300 leading-relaxed">
                    While we add modern conveniences like hotkeys, responsive design, and quality-of-life 
                    improvements, the core experience remains rooted in descriptive text, player imagination, 
                    and the classic command-driven interaction that makes MUDs special.
                  </p>
                </div>
              </div>

              {/* Immersive Experience */}
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div className="order-2 md:order-1">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center mr-3">
                      <Eye className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-white">Immersive Experience</h3>
                  </div>
                  <p className="text-gray-300 text-lg leading-relaxed mb-4">
                    Rather than creating a massive world with shallow content, we focus on crafting rich, 
                    detailed environments that reward exploration and multiple playthroughs. Every room, 
                    quest, and character serves the larger narrative.
                  </p>
                  <p className="text-gray-300 leading-relaxed">
                    Players should feel like they're part of a living world with depth and consequence. 
                    Your choices matter, your character grows meaningfully, and there's always something 
                    new to discover even in familiar places.
                  </p>
                </div>
                <div className="order-1 md:order-2 bg-gray-800 rounded-lg p-6 border border-gray-700">
                  <h4 className="text-lg font-semibold text-purple-400 mb-3">This Means:</h4>
                  <ul className="space-y-2 text-gray-300">
                    <li>• Quality over quantity in world design</li>
                    <li>• Meaningful choices with real consequences</li>
                    <li>• Replayability through different paths and classes</li>
                    <li>• Environmental storytelling and rich lore</li>
                  </ul>
                </div>
              </div>

              {/* Accessibility */}
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                  <h4 className="text-lg font-semibold text-orange-400 mb-3">This Means:</h4>
                  <ul className="space-y-2 text-gray-300">
                    <li>• Gentle learning curve for MUD newcomers</li>
                    <li>• Screen reader compatibility and keyboard navigation</li>
                    <li>• Clear tutorials and helpful community</li>
                    <li>• Honoring veteran player preferences and knowledge</li>
                  </ul>
                </div>
                <div>
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-orange-600 rounded-lg flex items-center justify-center mr-3">
                      <Users className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-white">Accessibility</h3>
                  </div>
                  <p className="text-gray-300 text-lg leading-relaxed mb-4">
                    HammerMUD should be accessible to everyone. This includes young players discovering MUDs for 
                    the first time, veteran players who appreciate text-based gaming, and players who rely on 
                    text interfaces for accessibility reasons.
                  </p>
                  <p className="text-gray-300 leading-relaxed">
                    We design with screen readers in mind, provide comprehensive help systems, and create 
                    onboarding experiences that welcome newcomers while respecting the depth that veterans expect. 
                    Everyone deserves to experience the magic of MUDs.
                  </p>
                </div>
              </div>

              {/* Transparency */}
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div className="order-2 md:order-1">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-cyan-600 rounded-lg flex items-center justify-center mr-3">
                      <FileText className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-white">Transparency</h3>
                  </div>
                  <p className="text-gray-300 text-lg leading-relaxed mb-4">
                    As a community-supported project, transparency isn't just nice to have - it's essential. 
                    Players investing their time and support deserve to know where the project is heading, 
                    how resources are used, and how decisions are made.
                  </p>
                  <p className="text-gray-300 leading-relaxed">
                    We maintain open development blogs, clear roadmaps, regular community updates, and 
                    honest communication about both successes and challenges. Your feedback directly 
                    influences development priorities.
                  </p>
                </div>
                <div className="order-1 md:order-2 bg-gray-800 rounded-lg p-6 border border-gray-700">
                  <h4 className="text-lg font-semibold text-cyan-400 mb-3">This Means:</h4>
                  <ul className="space-y-2 text-gray-300">
                    <li>• Public development roadmaps and progress updates</li>
                    <li>• Open discussion of design decisions</li>
                    <li>• Community involvement in feature prioritization</li>
                    <li>• Transparent use of Patreon support</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Name Callout Section */}
        <section className="py-20 bg-gray-800 border-t border-gray-700">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-8 text-center">
              <h2 className="text-2xl font-bold text-white mb-4">About That Name... 🔨</h2>
              <p className="text-gray-300 text-lg leading-relaxed mb-4">
                Yes, "HammerMUD" is admittedly a working title that I reached for based on my username. 
                It's about as creative as naming your pet dog "Dog" - functional, but not exactly inspired.
              </p>
              <p className="text-gray-300 leading-relaxed mb-6">
                The name might stick around (it's growing on me, honestly), or it might evolve into 
                something more fitting as the project develops. If you have brilliant naming ideas, 
                the community is always welcome to share them! Just don't suggest "MUDHammer" - 
                that's somehow even worse.
              </p>
              <div className="inline-block bg-gray-800 rounded-lg px-4 py-2 border border-gray-700">
                <span className="text-yellow-400 font-medium">⚠️ Name Subject to Change</span>
                <span className="text-gray-400 ml-2">(Please be kind)</span>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  )
}

export default AboutPage